/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, IconButton } from '@mui/material';
import React from 'react';
import type { ItemInterface } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import { DragHandle } from '@opengeoweb/theme';
import { DiagramTypeButton } from './DiagramTypeButton';
import { Soundings } from './Soundings';
import { WindBarbs } from './WindBarbs';
import type { SoundingFeature } from '../../api/edr-api/types';
import type { SoundingPanel } from '../../store/types';
import { HodographCollection } from '../Hodograph/HodographCollection';
import { SoundingsAccordion } from '../SoundingsAccordion/SoundingsAccordion';

interface SortableAccordionsProps {
  soundingPanelsInOrder: SoundingPanel[];
  data: SoundingFeature[][];
  size: number;
  chosenDiagramType: 'SKEWT' | 'BIJVOET' | 'EMAGRAM';
  setChosenDiagramType: (data: 'SKEWT' | 'BIJVOET' | 'EMAGRAM') => void;
  viewRef: React.RefObject<HTMLDivElement>;
  onOrderChange: (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ) => void;
}

type SortableItem = SoundingPanel & ItemInterface;

export const SortableAccordions = ({
  soundingPanelsInOrder,
  data,
  size,
  chosenDiagramType,
  setChosenDiagramType,
  viewRef,
  onOrderChange,
}: SortableAccordionsProps): React.ReactElement => {
  const [soundingsIsClosed, setSoundingsIsClosed] = React.useState(false);
  const [previousHeight, setPreviousHeight] = React.useState<number | null>(
    size,
  );

  const createPanel = React.useCallback(
    (id: string, isDisplayed: boolean): React.ReactElement | null => {
      const normalizedId = id.toLowerCase().replace(/\s+/g, '');
      switch (normalizedId) {
        case 'soundings':
          return (
            <SoundingsAccordion
              accordionIsOpen={true}
              diagramTypeButton={
                <DiagramTypeButton
                  diagramType={chosenDiagramType}
                  setDiagramType={setChosenDiagramType}
                />
              }
              isDisplayed={isDisplayed}
              name="Soundings"
              ref={viewRef}
              setPreviousHeight={setPreviousHeight}
              setSoundingsIsClosed={setSoundingsIsClosed}
              size={size}
            >
              <Soundings
                d3GraphId={normalizedId}
                data={data}
                diagramType={chosenDiagramType}
                svgDimensions={{
                  width: size,
                  height: size,
                  margin: { bottom: 25, left: 25, right: 25, top: 0 },
                }}
              />
            </SoundingsAccordion>
          );
        case 'windbarbs':
          return (
            <SoundingsAccordion
              isDisplayed={isDisplayed}
              maxWidth={200}
              name="Wind Barbs"
            >
              <WindBarbs
                animateResize={Boolean(soundingsIsClosed && previousHeight)}
                d3GraphId={normalizedId}
                data={data}
                diagramType={chosenDiagramType}
                svgDimensions={{
                  width: 100,
                  margin: { bottom: 25, left: 25, right: 25, top: 0 },
                  height: soundingsIsClosed ? (previousHeight ?? size) : size,
                }}
              />
            </SoundingsAccordion>
          );
        case 'hodograph':
          return (
            <SoundingsAccordion maxWidth={500} name="Hodograph">
              <HodographCollection data={data} />
            </SoundingsAccordion>
          );

        default:
          return null;
      }
    },
    [
      viewRef,
      size,
      chosenDiagramType,
      setChosenDiagramType,
      data,
      soundingsIsClosed,
      previousHeight,
    ],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        padding: 1,
      }}
    >
      <ReactSortable<SortableItem>
        animation={200}
        direction="horizontal"
        draggable=".sortable-item"
        handle=".drag-handle"
        list={soundingPanelsInOrder.map((panel) => ({ ...panel }))}
        setList={(list) =>
          onOrderChange(
            list.map(({ id, name, isDisplayed }) => ({
              id,
              name,
              isDisplayed,
            })),
          )
        }
        style={{
          display: 'flex',
          gap: 4,
          width: '100%',
        }}
        tag="div"
      >
        {soundingPanelsInOrder.flatMap(({ id, isDisplayed }) => {
          const panel = createPanel(id, isDisplayed);
          if (!panel) {
            return [];
          }
          return panel ? (
            <React.Fragment key={id}>
              {React.cloneElement(panel, {
                accordionId: id,
                isDisplayed,
                handleButtonComponent: (
                  <IconButton
                    aria-label="drag-handle"
                    className="drag-handle"
                    size="small"
                    sx={{ width: '24px', height: '24px' }}
                  >
                    <DragHandle fontSize="small" />
                  </IconButton>
                ),
              })}
            </React.Fragment>
          ) : null;
        })}
      </ReactSortable>
    </Box>
  );
};
